/**
 * @file 应用运行时装配：把 api-client、会话状态机、错误旁路接到一起。
 *
 * 为什么需要这一层：
 *   - 生成出来的 client 需要「取 token / 刷新 / 上报错误」三个钩子，而 store 又需要 client，
 *     直接互相 import 会成环；这里用 getter + 可变引用把依赖顺序摊平（规范 4.2 禁循环依赖）；
 *   - 三端只允许改这里的入参（端标识、基地址、是否 Mock），其余逻辑完全复用。
 */

import { ApiError, createApiClient, type ApiClient, type ClientType } from '../api';
import { createAuthStore, type AuthStore } from './store';

/** 运行时装配参数。 */
export interface AppRuntimeOptions {
  /** 端标识，写入 X-Client-Type 并决定存储键。 */
  clientType: ClientType;
  /** 端名，仅用于展示与日志前缀。 */
  appName: string;
  /** 后端基地址；不传则读 VITE_API_BASE_URL，仍为空则走同源（Vite 代理 / nginx）。 */
  baseUrl?: string;
}

/** 三端共享的运行时对象。 */
export interface AppRuntime {
  /** 端标识。 */
  readonly clientType: ClientType;
  /** 端名。 */
  readonly appName: string;
  /** 统一 api-client（组件禁止绕过它发请求）。 */
  readonly api: ApiClient;
  /** 会话状态机。 */
  readonly auth: AuthStore;
  /** 注册全局错误处理器（Toast 用），传 null 注销。 */
  setErrorHandler(handler: ((error: ApiError) => void) | null): void;
}

/**
 * @what 创建三端共用的运行时
 * @param options 装配参数
 * @return AppRuntime 运行时对象
 * @side 不写存储；首次 bootstrap 时才会读 localStorage
 */
export function createAppRuntime(options: AppRuntimeOptions): AppRuntime {
  const env = import.meta.env;
  const baseUrl = options.baseUrl ?? env.VITE_API_BASE_URL ?? '';

  /** 全局错误处理器：React 侧挂 Toast，未挂时静默（不影响抛出）。 */
  let errorHandler: ((error: ApiError) => void) | null = null;

  /**
   * @what 把 ApiError 交给当前处理器
   * @param error 业务错误
   * @return void 无返回值
   */
  const reportError = (error: ApiError): void => {
    errorHandler?.(error);
  };

  /** client 的可变引用：先建 store（store 只在被调用时才取 client），再回填。 */
  let apiRef: ApiClient | null = null;

  const auth = createAuthStore(() => {
    if (!apiRef) throw new Error('api-client 尚未初始化');
    return apiRef;
  });

  apiRef = createApiClient({
    baseUrl,
    clientType: options.clientType,
    getAccessToken: () => auth.getAccessToken(),
    onTokenExpired: () => auth.refreshSession(),
    onError: reportError,
  });

  return {
    clientType: options.clientType,
    appName: options.appName,
    api: apiRef,
    auth,

    setErrorHandler(handler: ((error: ApiError) => void) | null): void {
      errorHandler = handler;
    },
  };
}
